import React from 'react';
import { introwise } from 'common/types';
import { useCollectionData } from 'react-firebase-hooks/firestore';
import errorHandler from 'client/utils/error-handler';

import firebase from 'client/utils/firebase';
import { UserContext } from 'client/components/UserContext';
import { WebPushContext } from './WebPushContext';

type NotificationsContextType = {
  notifications: introwise.Notification[];
  loading: boolean;
  markRead: (query: Partial<introwise.Notification>) => void;
};

const NotificationsContext = React.createContext<NotificationsContextType>({
  notifications: [],
  loading: true,
  markRead: () => {},
});

const NotificationsProvider = ({
  children,
  notifications,
  loading,
  markRead,
}: {
  children: React.ReactNode;
  notifications: introwise.Notification[];
  loading: boolean;
  markRead: NotificationsContextType['markRead'];
}) => {
  let providedNotifications = notifications;
  const webPushContext = React.useContext(WebPushContext);
  const { enabled, dismissed, blocked, supported, enable, dismiss } = webPushContext;
  if (!enabled && !dismissed && !blocked && supported) {
    const webPushNotification: introwise.NotificationWebPushSuggestion = {
      type: 'webpush-suggestion',
      id: 'webpush-suggestion',
      enable,
      dismiss,
    };
    providedNotifications = providedNotifications.concat(webPushNotification);
  }

  return (
    <NotificationsContext.Provider value={{ notifications: providedNotifications, loading, markRead }}>
      {children}
    </NotificationsContext.Provider>
  );
};

const notificationsCollection = (id: string) =>
  firebase
    .firestore()
    .collection('users')
    .doc(id)
    .collection('notifications');

const NotificationsProviderContainer = ({ children }: { children: React.ReactNode }) => {
  const { userId, loggingIn } = React.useContext(UserContext);
  const [data, loading, error] = useCollectionData<introwise.Notification>(
    userId
      ? notificationsCollection(userId)
          .where('read', '==', false)
          .where('inApp', '==', true)
      : null,
    {
      idField: 'id',
    },
  );

  React.useEffect(() => {
    if (error) {
      errorHandler.report(`Failed to get notifications: ${error}`);
    }
  }, [error]);

  const notifications = data || []; // TODO: convert here

  const markRead: NotificationsContextType['markRead'] = query => {
    if (notifications.length === 0) {
      return;
    }
    const notificationFound = notifications.find(n => {
      // eslint-disable-next-line no-restricted-syntax
      for (const key of Object.keys(query)) {
        // @ts-ignore
        if (n[key] !== query[key]) {
          return false;
        }
      }
      return true;
    });
    if (notificationFound) {
      notificationsCollection(userId)
        .doc(notificationFound.id)
        .update({ read: true })
        .catch(err => errorHandler.report(`Failed to mark notification as read: ${err}`));
    }
  };

  return (
    <NotificationsProvider loading={loading || loggingIn} notifications={notifications} markRead={markRead}>
      {children}
    </NotificationsProvider>
  );
};

const NotificationsConsumer = NotificationsContext.Consumer;

export { NotificationsProviderContainer as NotificationsProvider, NotificationsConsumer, NotificationsContext };
